import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart3, Lock, ChevronDown, ChevronRight, Download, Share2, Eye, X,
  Loader2, TrendingUp, TrendingDown, Minus, Send, FileText, AlertTriangle,
  ShieldAlert, ShieldCheck, Zap, Building2, Sparkles, Scale, BadgeCheck,
  Briefcase, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { fmtMoney, DEFAULT_CURRENCY } from "../lib/money";
import { Button } from "../components/ui/button";
import { ScrollPanel } from "../components/ui/scroll-panel";
import EmptyState from "../components/common/EmptyState";

const LUCIDE_MAP = {
  ShieldAlert, ShieldCheck, Zap, FileText, Building2, Sparkles, Scale,
  BadgeCheck, Briefcase, AlertTriangle, TrendingUp, TrendingDown,
};

function Lucide({ name, size = 16, className = "", strokeWidth = 1.75 }) {
  const Icon = LUCIDE_MAP[name] || FileText;
  return <Icon size={size} className={className} strokeWidth={strokeWidth} />;
}

/* ========================================================================
   QENVA · Rapports de Vigilance™
   Immutable monthly snapshots. Stored in `vigilance_reports` collection,
   surfaced in a Figma-validated layout: Hero KPI (4-month progression) +
   monthly table with delta pills + fullscreen modal for the detail view.
======================================================================== */
export default function VigilanceReports() {
  const { t, i18n } = useTranslation("vigilance_reports");
  const locale = (i18n.language || "fr").startsWith("fr") ? "fr" : "en";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMonth, setModalMonth] = useState(null);
  const [modalHtml, setModalHtml] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMonth, setShareMonth] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/vigilance-reports/list", {
        params: { limit: 12, locale },
      });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("toasts.loaded_error"));
    } finally {
      setLoading(false);
    }
  }, [locale, t]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.post("/vigilance-reports/generate", { locale });
      toast.success(t("toasts.generated"));
      await fetchList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("toasts.generation_error"));
    } finally {
      setGenerating(false);
    }
  };

  const openModal = async (month) => {
    setModalMonth(month);
    setModalOpen(true);
    setModalLoading(true);
    try {
      const r = await api.post(`/vigilance-reports/${month}/preview-html`, null, {
        params: { locale },
      });
      setModalHtml(r.data?.html || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setModalOpen(false);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalHtml("");
    setModalMonth(null);
  };

  const downloadPdf = async (month) => {
    try {
      const r = await api.get(`/vigilance-reports/${month}/pdf`, {
        params: { locale },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `QENVA-Vigilance-${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("toasts.download_error"));
    }
  };

  const openShare = (month) => {
    setShareMonth(month);
    setShareOpen(true);
  };

  // Hero KPI — "Progression Guardian™ sur 4 mois"
  const progression = useMemo(() => {
    if (!items.length) return { hasHistory: false, latest: 0, baseline: 0, points: [], delta: 0, trend: "flat" };
    const ordered = [...items].sort((a, b) => a.month.localeCompare(b.month));
    const last4 = ordered.slice(-4);
    const latest = last4[last4.length - 1]?.guardian_score || 0;
    const baseline = last4[0]?.guardian_score || latest;
    const delta = latest - baseline;
    const trend = delta > 1 ? "up" : delta < -1 ? "down" : "flat";
    return {
      hasHistory: last4.length >= 2,
      latest,
      baseline,
      points: last4.map((it) => ({
        month: it.month,
        label: locale === "fr" ? it.month_label_fr : it.month_label_en,
        score: it.guardian_score,
      })),
      delta,
      trend,
    };
  }, [items, locale]);

  const latestItem = useMemo(
    () => items.find((it) => it.is_latest) || items[0] || null,
    [items],
  );

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 max-w-7xl mx-auto" data-testid="vigilance-page">
      {/* ----------------------------- Header ----------------------------- */}
      <div className="mb-7 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-qenva-blue inline-flex items-center gap-1.5">
              <BarChart3 size={12} strokeWidth={1.75} />
              {t("page.kicker")}
            </p>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 text-[10.5px] font-bold uppercase tracking-wider">
              <Lock size={9} /> Confidentiel
            </span>
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-qenva-navy"
            data-testid="vigilance-title"
          >
            {t("page.title")}
          </h1>
          <p className="text-[15px] text-zinc-500 mt-2">{t("page.subtitle")}</p>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={generating}
          className="bg-qenva-blue hover:bg-qenva-blue/90 self-start"
          data-testid="vigilance-generate-btn"
        >
          {generating
            ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> {t("page.generate_loading")}</>
            : <><RefreshCw size={14} className="mr-1.5" /> {t("page.generate_button")}</>}
        </Button>
      </div>

      {/* ----------------------------- Hero KPI --------------------------- */}
      <HeroKpi progression={progression} latestItem={latestItem} locale={locale} t={t} />

      {/* ----------------------------- Table ------------------------------ */}
      <section className="mt-10" data-testid="vigilance-table-section">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display text-xl font-semibold text-qenva-navy">
              {t("table.title")}
            </h2>
            <p className="text-[13px] text-zinc-500">{t("table.subtitle")}</p>
          </div>
        </div>

        {loading ? (
          <SkeletonTable />
        ) : items.length === 0 ? (
          <VigilanceEmptyState onGenerate={handleGenerate} generating={generating} t={t} />
        ) : (
          <ReportsTable
            items={items}
            locale={locale}
            expandedMonth={expandedMonth}
            onToggle={(m) => setExpandedMonth((cur) => (cur === m ? null : m))}
            onOpen={openModal}
            onPdf={downloadPdf}
            onShare={openShare}
            t={t}
          />
        )}
      </section>

      {/* ----------------------------- Modal ------------------------------ */}
      {modalOpen && (
        <VigilanceFullReportModal
          month={modalMonth}
          html={modalHtml}
          loading={modalLoading}
          onClose={closeModal}
          onPdf={() => downloadPdf(modalMonth)}
          onShare={() => openShare(modalMonth)}
          t={t}
          locale={locale}
          item={items.find((it) => it.month === modalMonth)}
        />
      )}

      {/* ----------------------------- Share dialog ---------------------- */}
      {shareOpen && (
        <ShareDialog
          month={shareMonth}
          locale={locale}
          onClose={() => setShareOpen(false)}
          onSent={() => { setShareOpen(false); fetchList(); }}
          t={t}
        />
      )}
    </div>
  );
}

/* ----------------------------- HeroKpi ---------------------------------- */
function HeroKpi({ progression, latestItem, locale, t }) {
  const trendIcon = progression.trend === "up"
    ? <TrendingUp size={18} strokeWidth={2} />
    : progression.trend === "down"
      ? <TrendingDown size={18} strokeWidth={2} />
      : <Minus size={18} strokeWidth={2} />;
  const trendColor = progression.trend === "up"
    ? "text-emerald-600"
    : progression.trend === "down"
      ? "text-rose-600"
      : "text-zinc-500";
  const trendBg = progression.trend === "up"
    ? "bg-emerald-50 border-emerald-100"
    : progression.trend === "down"
      ? "bg-rose-50 border-rose-100"
      : "bg-zinc-50 border-zinc-100";
  const trendLabel = progression.trend === "up"
    ? t("hero.trend_up")
    : progression.trend === "down"
      ? t("hero.trend_down")
      : t("hero.trend_flat");

  const maxScore = 100;
  const totalAlerts = latestItem?.critical_count || 0;
  const cumulativePreserved = latestItem?.value_preserved_cumulative || 0;

  return (
    <section
      className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr_1fr] gap-4"
      data-testid="vigilance-hero-kpi"
    >
      {/* Primary Hero KPI: Progression Guardian™ sur 4 mois */}
      <div className="relative overflow-hidden rounded-2xl bg-qenva-navy text-white p-6 sm:p-8 shadow-lg">
        <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-emerald-400/10 blur-2xl" />
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-300">
          {t("hero.kpi_label")}
        </p>
        <div className="flex items-end gap-3 mt-3">
          <span
            className="font-display text-5xl sm:text-6xl font-semibold leading-none"
            data-testid="vigilance-hero-latest-score"
          >
            {progression.latest}
          </span>
          <span className="text-white/50 text-lg pb-2">/100</span>
        </div>

        {progression.hasHistory ? (
          <div className={`inline-flex items-center gap-1.5 mt-4 px-3 py-1 rounded-full bg-white/10 ${trendColor === "text-emerald-600" ? "text-emerald-300" : trendColor === "text-rose-600" ? "text-rose-300" : "text-white/70"}`}>
            {trendIcon}
            <span className="text-[12px] font-semibold">
              {progression.delta > 0 ? "+" : ""}{progression.delta} pts {t("hero.vs_first_month")}
            </span>
          </div>
        ) : (
          <p className="mt-4 text-[12.5px] text-white/60">{t("hero.no_history")}</p>
        )}

        {/* Mini sparkline of last 4 months */}
        {progression.points.length > 0 && (
          <div className="mt-6 relative" data-testid="vigilance-spark">
            <SparkLine points={progression.points} max={maxScore} />
            <div className="flex justify-between mt-3 text-[11px] text-white/50">
              {progression.points.map((p) => (
                <span key={p.month} className="font-medium">{p.label?.split(" ")[0] || p.month}</span>
              ))}
            </div>
          </div>
        )}

        <p className="mt-4 text-[12.5px] text-white/60 max-w-md">
          {trendLabel}
        </p>
      </div>

      {/* Secondary cards */}
      <SecondaryKpi
        label={t("hero.secondary_alerts")}
        value={totalAlerts}
        color="rose"
        icon="ShieldAlert"
        testId="vigilance-secondary-alerts"
      />
      <SecondaryKpi
        label={t("hero.secondary_preserved")}
        value={fmtMoney(cumulativePreserved, latestItem?.currency || DEFAULT_CURRENCY) || "—"}
        color="emerald"
        icon="Sparkles"
        testId="vigilance-secondary-preserved"
      />
    </section>
  );
}

function SecondaryKpi({ label, value, color, icon, testId }) {
  const ringByColor = {
    rose: "bg-rose-50 text-rose-600",
    emerald: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
  };
  return (
    <div
      className="rounded-2xl bg-white border border-zinc-200 p-5 flex flex-col justify-between min-h-[180px]"
      data-testid={testId}
    >
      <div className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${ringByColor[color] || ringByColor.blue}`}>
        <Lucide name={icon} size={16} />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-zinc-400">{label}</p>
        <p className="font-display text-3xl text-qenva-navy mt-1 font-semibold">{value}</p>
      </div>
    </div>
  );
}

function SparkLine({ points, max = 100 }) {
  if (!points || points.length < 2) return null;
  const width = 320;
  const height = 60;
  const padding = 8;
  const xStep = (width - padding * 2) / (points.length - 1);
  const ys = points.map((p, i) => ({
    x: padding + i * xStep,
    y: height - padding - ((p.score || 0) / max) * (height - padding * 2),
  }));
  const pathD = ys.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-16">
      <defs>
        <linearGradient id="vigilanceArea" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(16,185,129,0.4)" />
          <stop offset="100%" stopColor="rgba(16,185,129,0)" />
        </linearGradient>
      </defs>
      <path
        d={`${pathD} L${ys[ys.length - 1].x} ${height - padding} L${ys[0].x} ${height - padding} Z`}
        fill="url(#vigilanceArea)"
      />
      <path d={pathD} fill="none" stroke="#10B981" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      {ys.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3.4" fill="#10B981" stroke="#ffffff" strokeWidth="2" />
      ))}
    </svg>
  );
}

/* ----------------------------- ReportsTable ----------------------------- */
function ReportsTable({ items, locale, expandedMonth, onToggle, onOpen, onPdf, onShare, t }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden" data-testid="vigilance-table">
      {/* Desktop header */}
      <div className="hidden sm:grid grid-cols-[1.4fr_0.8fr_1fr_1fr_0.6fr_0.6fr_auto] gap-3 px-5 py-3 bg-zinc-50/80 border-b border-zinc-200 text-[11px] uppercase tracking-[0.14em] font-bold text-zinc-500">
        <span>{t("table.col_month")}</span>
        <span>{t("table.col_score")}</span>
        <span>{t("table.col_under_watch")}</span>
        <span>{t("table.col_preserved")}</span>
        <span>{t("table.col_critical")}</span>
        <span>{t("table.col_opportunities")}</span>
        <span className="text-right">{t("table.col_actions")}</span>
      </div>

      <ul>
        {items.map((it) => (
          <ReportRow
            key={it.month}
            item={it}
            locale={locale}
            expanded={expandedMonth === it.month}
            onToggle={() => onToggle(it.month)}
            onOpen={() => onOpen(it.month)}
            onPdf={() => onPdf(it.month)}
            onShare={() => onShare(it.month)}
            t={t}
          />
        ))}
      </ul>
    </div>
  );
}

function DeltaPill({ pill, "data-testid": dataTestId }) {
  if (!pill) return null;
  const color = pill.startsWith("+") ? "text-emerald-600 bg-emerald-50"
    : pill.startsWith("-") ? "text-rose-600 bg-rose-50"
      : "text-zinc-500 bg-zinc-100";
  return (
    <span
      className={`inline-block ml-1 px-1.5 py-0.5 rounded-full text-[10.5px] font-bold ${color}`}
      data-testid={dataTestId}
    >
      {pill}
    </span>
  );
}

function ReportRow({ item, locale, expanded, onToggle, onOpen, onPdf, onShare, t }) {
  const monthLabel = locale === "fr" ? item.month_label_fr : item.month_label_en;
  return (
    <li
      className="border-b border-zinc-100 last:border-0"
      data-testid={`vigilance-row-${item.month}`}
    >
      {/* Row content (clickable to toggle accordion) */}
      <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_0.8fr_1fr_1fr_0.6fr_0.6fr_auto] gap-3 px-5 py-4 items-center hover:bg-zinc-50/50 transition-colors">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 text-left min-w-0"
          data-testid={`vigilance-toggle-${item.month}`}
        >
          {expanded
            ? <ChevronDown size={15} className="text-zinc-400 shrink-0" />
            : <ChevronRight size={15} className="text-zinc-400 shrink-0" />}
          <div className="min-w-0">
            <p className="font-semibold text-qenva-navy truncate">{monthLabel}</p>
            {item.is_latest && (
              <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[9.5px] font-bold uppercase tracking-wider">
                {t("page.latest_badge")}
              </span>
            )}
          </div>
        </button>
        <div className="flex items-center">
          <span className="font-display text-xl font-semibold text-qenva-navy">{item.guardian_score}</span>
          <span className="text-zinc-400 text-[12px] ml-0.5">/100</span>
          <DeltaPill pill={item.display?.guardian_score_delta_pill} data-testid={`vigilance-delta-score-${item.month}`} />
        </div>
        <div className="text-[14px] text-qenva-navy font-medium">
          {item.display?.value_under_watch_formatted || "—"}
          <DeltaPill pill={item.display?.value_under_watch_delta_pill} data-testid={`vigilance-delta-watch-${item.month}`} />
        </div>
        <div className="text-[14px] text-emerald-700 font-medium">
          {item.display?.value_preserved_period_formatted || "—"}
          <DeltaPill pill={item.display?.value_preserved_delta_pill} data-testid={`vigilance-delta-preserved-${item.month}`} />
        </div>
        <div>
          <span className="text-rose-600 font-semibold">{item.critical_count}</span>
          <DeltaPill pill={item.display?.critical_delta_pill} data-testid={`vigilance-delta-critical-${item.month}`} />
        </div>
        <div>
          <span className="text-amber-600 font-semibold">{item.opportunities_count}</span>
          <DeltaPill pill={item.display?.opportunities_delta_pill} data-testid={`vigilance-delta-opps-${item.month}`} />
        </div>
        <div className="flex items-center gap-2 justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={onPdf}
            data-testid={`vigilance-pdf-${item.month}`}
            className="hidden sm:inline-flex"
          >
            <Download size={13} className="mr-1" /> {t("table.pdf_button")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onShare}
            data-testid={`vigilance-share-${item.month}`}
            className="hidden md:inline-flex"
          >
            <Share2 size={13} className="mr-1" /> {t("table.share_button")}
          </Button>
          <Button
            size="sm"
            onClick={onOpen}
            className="bg-qenva-blue hover:bg-qenva-blue/90"
            data-testid={`vigilance-open-${item.month}`}
          >
            <Eye size={13} className="mr-1" /> {t("table.open_button")}
          </Button>
        </div>
      </div>

      {/* Accordion detail panel */}
      {expanded && (
        <ReportAccordionDetail
          item={item}
          locale={locale}
          onShare={onShare}
          t={t}
        />
      )}
    </li>
  );
}

function ReportAccordionDetail({ item, locale, onShare, t }) {
  const deltas = item.deltas || {};
  return (
    <div
      className="px-5 pb-5 grid grid-cols-1 lg:grid-cols-2 gap-5 bg-zinc-50/50"
      data-testid={`vigilance-accordion-${item.month}`}
    >
      {/* Alerts of the month */}
      <DetailCard title={t("accordion.alerts_title")} icon="ShieldAlert">
        {(item.month_alerts || []).length === 0 ? (
          <p className="text-[13px] text-zinc-500 italic">{t("accordion.no_alerts")}</p>
        ) : (
          <ul className="space-y-2">
            {(item.month_alerts || []).slice(0, 5).map((a) => (
              <li key={a.id} className="flex items-start gap-2.5 text-[13px]" data-testid={`vigilance-alert-${item.month}-${a.id}`}>
                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                  a.severity === "Critical" ? "bg-rose-100 text-rose-700"
                    : a.severity === "High" ? "bg-amber-100 text-amber-700"
                      : "bg-zinc-100 text-zinc-600"
                }`}>{a.severity}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-qenva-navy truncate">{a.title}</p>
                  {a.subtitle && <p className="text-zinc-500 text-[12px] truncate">{a.subtitle}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </DetailCard>

      {/* Recommendations */}
      <DetailCard title={t("accordion.recommendations_title")} icon="Sparkles">
        {(item.recommendations || []).length === 0 ? (
          <p className="text-[13px] text-zinc-500 italic">{t("accordion.no_recommendations")}</p>
        ) : (
          <ul className="space-y-3">
            {(item.recommendations || []).map((r) => (
              <li
                key={r.alert_id || r.rank}
                className="flex items-start gap-3 text-[13px]"
                data-testid={`vigilance-reco-${item.month}-${r.rank}`}
              >
                <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-[12px] font-bold">
                  #{r.rank}
                </span>
                <div className="min-w-0">
                  <p className="font-medium text-qenva-navy">{r.decision_recommended}</p>
                  <p className="text-[12px] text-zinc-500">
                    {t("accordion.impact")}: <strong>{fmtMoney(r.impact_estimated, item?.currency || DEFAULT_CURRENCY) || "—"}</strong> · {t("accordion.confidence")}: <strong>{r.confidence}%</strong>
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DetailCard>

      {/* Deadlines */}
      <DetailCard title={t("accordion.deadlines_title")} icon="AlertTriangle">
        {(item.deadlines || []).length === 0 ? (
          <p className="text-[13px] text-zinc-500 italic">{t("accordion.no_deadlines")}</p>
        ) : (
          <ul className="space-y-2">
            {(item.deadlines || []).slice(0, 5).map((d, idx) => (
              <li key={idx} className="flex items-center gap-2.5 text-[13px]">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                  d.color === "rose" ? "bg-rose-500"
                    : d.color === "amber" ? "bg-amber-500"
                      : "bg-blue-500"
                }`} />
                <p className="flex-1 truncate text-qenva-navy font-medium">{d.title}</p>
                <span className="text-[12px] text-zinc-500 shrink-0">{(d.due_at || "").slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        )}
      </DetailCard>

      {/* Evolution panel */}
      <DetailCard title={t("accordion.evolution_title")} icon="TrendingUp">
        {!deltas?.has_previous ? (
          <p className="text-[13px] text-zinc-500 italic">{t("accordion.no_evolution")}</p>
        ) : (
          <ul className="space-y-1.5 text-[13px]">
            <EvolutionRow label={t("table.col_score")} pill={item.display?.guardian_score_delta_pill} suffix=" pts" />
            <EvolutionRow label={t("table.col_under_watch")} pill={item.display?.value_under_watch_delta_pill} />
            <EvolutionRow label={t("table.col_preserved")} pill={item.display?.value_preserved_delta_pill} />
            <EvolutionRow label={t("table.col_critical")} pill={item.display?.critical_delta_pill} />
            <EvolutionRow label={t("table.col_opportunities")} pill={item.display?.opportunities_delta_pill} />
          </ul>
        )}
      </DetailCard>

      <div className="lg:col-span-2 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={onShare}
          data-testid={`vigilance-row-share-${item.month}`}
        >
          <Share2 size={13} className="mr-1.5" /> {t("table.share_button")}
        </Button>
      </div>
    </div>
  );
}

function DetailCard({ title, icon, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4">
      <p className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-zinc-500 mb-3 flex items-center gap-1.5">
        <Lucide name={icon} size={12} className="text-zinc-400" />
        {title}
      </p>
      {children}
    </div>
  );
}

function EvolutionRow({ label, pill, suffix = "" }) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-zinc-600">{label}</span>
      <span className="font-semibold">{pill || "—"}{suffix}</span>
    </li>
  );
}

/* ----------------------------- Full Report Modal ----------------------- */
function VigilanceFullReportModal({ month, html, loading, onClose, onPdf, onShare, t, locale, item }) {
  // Lock body scroll while modal open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const monthLabel = item
    ? (locale === "fr" ? item.month_label_fr : item.month_label_en)
    : month;

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-900/70 backdrop-blur-sm flex items-stretch justify-center"
      onClick={onClose}
      data-testid="vigilance-full-modal"
    >
      <div
        className="bg-white w-full sm:max-w-5xl sm:my-6 sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3 shrink-0">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.16em] font-bold text-qenva-blue">
              {t("page.kicker")}
            </p>
            <h2 className="font-display text-base sm:text-lg font-semibold text-qenva-navy truncate">
              {monthLabel} {t("modal.title_suffix")}
            </h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={onPdf} data-testid="vigilance-modal-pdf">
              <Download size={13} className="mr-1.5" /> {t("modal.download_pdf")}
            </Button>
            <Button size="sm" onClick={onShare} className="bg-qenva-blue hover:bg-qenva-blue/90" data-testid="vigilance-modal-share">
              <Share2 size={13} className="mr-1.5" /> {t("modal.share")}
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-zinc-100 text-zinc-500"
              data-testid="vigilance-modal-close"
              aria-label={t("modal.close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <ScrollPanel className="bg-zinc-100">
          {loading ? (
            <div className="flex items-center justify-center h-full py-24 text-zinc-500">
              <Loader2 size={18} className="animate-spin mr-2" />
              {t("modal.loading")}
            </div>
          ) : (
            <iframe
              srcDoc={html}
              title={`${monthLabel} ${t("modal.title_suffix")}`}
              sandbox="allow-same-origin"
              className="w-full min-h-[1400px] border-0 bg-white"
              data-testid="vigilance-modal-iframe"
            />
          )}
        </ScrollPanel>
      </div>
    </div>
  );
}

/* ----------------------------- Share Dialog ----------------------------- */
function ShareDialog({ month, locale, onClose, onSent, t }) {
  const [recipients, setRecipients] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const list = recipients.split(",").map((s) => s.trim()).filter(Boolean);
    if (!list.length) {
      toast.error(t("share.error_no_recipients"));
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/vigilance-reports/${month}/share`, {
        recipient_emails: list,
        note: note || null,
      }, { params: { locale } });
      const results = r.data?.results || [];
      const skipped = results.filter((x) => x.status === "skipped");
      if (skipped.length === results.length && skipped.length > 0) {
        toast.warning(t("share.dry_run_note"));
      } else {
        toast.success(t("share.success"));
      }
      onSent && onSent();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Erreur");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-900/60 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="vigilance-share-dialog"
    >
      <div
        className="bg-white max-w-md w-full rounded-2xl shadow-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-lg font-semibold text-qenva-navy">{t("share.title")}</h3>
        <p className="text-[12.5px] text-zinc-500 mt-1 mb-4">{t("share.subtitle")}</p>
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="text-[12px] uppercase tracking-wider font-semibold text-zinc-600">
              {t("share.label_recipients")}
            </span>
            <textarea
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              rows={2}
              placeholder={t("share.placeholder_recipients")}
              className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-qenva-blue/30"
              data-testid="vigilance-share-recipients"
            />
          </label>
          <label className="block">
            <span className="text-[12px] uppercase tracking-wider font-semibold text-zinc-600">
              {t("share.label_note")}
            </span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={t("share.placeholder_note")}
              className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-qenva-blue/30"
              data-testid="vigilance-share-note"
            />
          </label>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={sending}
              className="bg-qenva-blue hover:bg-qenva-blue/90"
              data-testid="vigilance-share-send"
            >
              {sending
                ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> {t("share.sending")}</>
                : <><Send size={13} className="mr-1.5" /> {t("share.send_button")}</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ----------------------------- Misc ------------------------------------ */
function SkeletonTable() {
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-3" data-testid="vigilance-skeleton">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-12 rounded-lg bg-zinc-100 animate-pulse" />
      ))}
    </div>
  );
}

function VigilanceEmptyState({ onGenerate, generating, t }) {
  return (
    <EmptyState
      icon={BarChart3}
      title={t("page.no_reports_title")}
      subtitle={t("page.no_reports_subtitle")}
      action={{
        label: generating ? t("page.generate_loading") : t("page.generate_button"),
        onClick: onGenerate,
        loading: generating,
        disabled: generating,
        icon: generating ? Loader2 : RefreshCw,
        testId: "vigilance-empty-generate-btn",
      }}
      testId="vigilance-empty"
    />
  );
}
